import { useMemo, useState } from "react";
import { useCopySequences, useMerchantCopyData } from "@/api/admin/copy-sequences";
import { useAdminMerchants } from "@/api/admin/merchants";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Select } from "@/components/base/select/select";
import { error, success } from "@/providers/ToastProvider";
import { TabHeaderReadOnly } from "./tab-header";
import type { MerchantWithUsers } from "./types";

type CopySequencesTabProps = {
  merchant: MerchantWithUsers;
};

export const CopySequencesTab = ({ merchant }: CopySequencesTabProps) => {
  const [targetMerchantId, setTargetMerchantId] = useState<string | null>(null);
  const [selectedSequenceIds, setSelectedSequenceIds] = useState<Set<string>>(new Set());
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(new Set());

  const { data: merchantsData } = useAdminMerchants({ offset: 0, limit: 200 }, { column: "id", direction: "asc" }, {});
  const { data: copyData, isLoading } = useMerchantCopyData(merchant.id);
  const { mutateAsync, isPending, data: result } = useCopySequences();

  const merchants = merchantsData?.merchants ?? [];
  const targetMerchantItems = merchants
    .filter((m) => m.id !== merchant.id)
    .map((m) => ({ id: m.id, label: `${m.name} (${m.id})` }));
  const sourceSequences = copyData?.sequences ?? [];
  const sourceTemplates = copyData?.messageTemplates ?? [];

  // Template IDs required by selected sequences (cannot be deselected)
  const requiredTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const seq of sourceSequences) {
      if (selectedSequenceIds.has(seq.id)) {
        for (const tid of seq.dependentTemplateIds) {
          ids.add(tid);
        }
      }
    }
    return ids;
  }, [selectedSequenceIds, sourceSequences]);

  const toggleSequence = (id: string) => {
    setSelectedSequenceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleTemplate = (id: string) => {
    if (requiredTemplateIds.has(id)) return;
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAllSequences = () => {
    if (selectedSequenceIds.size === sourceSequences.length) {
      setSelectedSequenceIds(new Set());
    } else {
      setSelectedSequenceIds(new Set(sourceSequences.map((s) => s.id)));
    }
  };

  const allTemplateIds = useMemo(() => new Set(sourceTemplates.map((t) => t.id)), [sourceTemplates]);
  const effectiveTemplateIds = useMemo(
    () => new Set([...selectedTemplateIds, ...requiredTemplateIds]),
    [selectedTemplateIds, requiredTemplateIds],
  );

  const toggleAllTemplates = () => {
    if (effectiveTemplateIds.size === sourceTemplates.length) {
      // Deselect only non-required ones
      setSelectedTemplateIds(new Set());
    } else {
      // Select all that aren't already required
      const manualIds = new Set<string>();
      for (const id of allTemplateIds) {
        if (!requiredTemplateIds.has(id)) {
          manualIds.add(id);
        }
      }
      setSelectedTemplateIds(manualIds);
    }
  };

  const handleSubmit = async () => {
    if (!targetMerchantId) {
      error("Please select a target merchant");
      return;
    }
    if (selectedSequenceIds.size === 0 && effectiveTemplateIds.size === 0) {
      error("Please select at least one sequence or template");
      return;
    }
    try {
      const sequenceIds = selectedSequenceIds.size === sourceSequences.length ? undefined : [...selectedSequenceIds];
      const templateIds = effectiveTemplateIds.size === sourceTemplates.length ? undefined : [...effectiveTemplateIds];
      const res = await mutateAsync({
        sourceMerchantId: merchant.id,
        targetMerchantId,
        sequenceIds: selectedSequenceIds.size > 0 ? sequenceIds : [],
        templateIds: effectiveTemplateIds.size > 0 ? templateIds : [],
      });
      success(
        `Copied ${res.sequences.length} sequences, ${res.messageTemplates.length} templates, ${res.defaults.length} defaults`,
      );
    } catch {
      error("Failed to copy sequences");
    }
  };

  const totalSelected = selectedSequenceIds.size + effectiveTemplateIds.size;

  return (
    <div className="flex flex-col gap-6">
      <TabHeaderReadOnly
        title="Copy Sequences & Templates"
        subtitle="Copy sequences and message templates to another merchant. Selecting a sequence will automatically include its dependent templates."
      />

      {isLoading ? (
        <p className="text-sm text-tertiary">Loading...</p>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* Sequences column */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-secondary">Sequences</label>
              {sourceSequences.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllSequences}
                  className="text-sm text-brand-secondary hover:underline"
                >
                  {selectedSequenceIds.size === sourceSequences.length ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            {sourceSequences.length === 0 ? (
              <p className="text-sm text-tertiary">No sequences found.</p>
            ) : (
              <div className="flex flex-col gap-2 rounded-lg border border-secondary p-3">
                {sourceSequences.map((seq) => (
                  <Checkbox
                    key={seq.id}
                    isSelected={selectedSequenceIds.has(seq.id)}
                    onChange={() => toggleSequence(seq.id)}
                    label={seq.name}
                    hint={seq.type}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Templates column */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-secondary">Message Templates</label>
              {sourceTemplates.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllTemplates}
                  className="text-sm text-brand-secondary hover:underline"
                >
                  {effectiveTemplateIds.size === sourceTemplates.length ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            {sourceTemplates.length === 0 ? (
              <p className="text-sm text-tertiary">No message templates found.</p>
            ) : (
              <div className="flex flex-col gap-2 rounded-lg border border-secondary p-3">
                {sourceTemplates.map((tmpl) => {
                  const isRequired = requiredTemplateIds.has(tmpl.id);
                  return (
                    <Checkbox
                      key={tmpl.id}
                      isSelected={effectiveTemplateIds.has(tmpl.id)}
                      isDisabled={isRequired}
                      onChange={() => toggleTemplate(tmpl.id)}
                      label={tmpl.name}
                      hint={isRequired ? `${tmpl.category} — required by selected sequence` : tmpl.category}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <Select
        label="Target Merchant"
        placeholder="Select target merchant"
        isRequired
        size="md"
        items={targetMerchantItems}
        selectedKey={targetMerchantId}
        onSelectionChange={(v) => setTargetMerchantId(v as string)}
      >
        {(item) => (
          <Select.Item key={item.id} id={item.id}>
            {item.label}
          </Select.Item>
        )}
      </Select>

      {totalSelected > 0 && (
        <p className="text-sm text-tertiary">
          {selectedSequenceIds.size > 0 &&
            `${selectedSequenceIds.size} sequence${selectedSequenceIds.size !== 1 ? "s" : ""}`}
          {selectedSequenceIds.size > 0 && effectiveTemplateIds.size > 0 && " and "}
          {effectiveTemplateIds.size > 0 &&
            `${effectiveTemplateIds.size} message template${effectiveTemplateIds.size !== 1 ? "s" : ""}`}
          {" will be copied."}
        </p>
      )}

      <Button
        color="primary"
        size="lg"
        onClick={handleSubmit}
        isDisabled={isPending || !targetMerchantId || totalSelected === 0}
      >
        {isPending ? "Copying..." : "Copy"}
      </Button>

      {result && (
        <div className="flex flex-col gap-4 rounded-lg border border-secondary p-4">
          <h2 className="text-md font-semibold text-primary">Results</h2>

          {result.messageTemplates.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-secondary mb-2">
                Message Templates ({result.messageTemplates.length})
              </h3>
              <ul className="text-sm text-tertiary space-y-1">
                {result.messageTemplates.map((t) => (
                  <li key={t.newId}>{t.name}</li>
                ))}
              </ul>
            </div>
          )}

          {result.sequences.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-secondary mb-2">Sequences ({result.sequences.length})</h3>
              <ul className="text-sm text-tertiary space-y-1">
                {result.sequences.map((s) => (
                  <li key={s.newId}>{s.name}</li>
                ))}
              </ul>
            </div>
          )}

          {result.defaults.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-secondary mb-2">Defaults ({result.defaults.length})</h3>
              <ul className="text-sm text-tertiary space-y-1">
                {result.defaults.map((d) => (
                  <li key={d.triggerType}>
                    {d.triggerType} → {d.sequenceId}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
